/**
 * Moving one origin bank account between payment providers as an operations flow (ADR 0027,
 * ADR 0030 §2): `op/bank-accounts/:id/payment-provider-migration`. The preview reports what
 * blocks the move and what it will change; the execution maps the account at the hub if asked,
 * switches the provider, sets the account up again and records how the old provider's
 * capability rows stood, so moving back restores them. Admin-only (`integration:manage`).
 */

import type { PaymentDispatchProviderId } from './ap/payment-dispatch-providers.js';
import type { ProviderSwitchBlockedDetails } from './ap/payments.js';
import type {
  BankAccountPaymentSetupStatus,
  PaymentCapabilityStatus,
  PaymentInstructionKind,
} from './generated/enums.js';

/**
 * Refusal of a migration whose preview names blockers: a 409 whose `details` are
 * {@link PaymentProviderMigrationBlockers}. Nothing was changed.
 */
export const PROVIDER_MIGRATION_BLOCKED = 'PROVIDER_MIGRATION_BLOCKED';

/**
 * Why a migration cannot run now, one code per reason. Admin screens word them.
 *
 * - `IN_FLIGHT`: money from the account may still move (the ids are in `openSendIds` and
 *   `unresolvedAttemptIds`). A finished payment left on an unsettled manifest does not count.
 * - `MOVE_NOT_SUPPORTED`: the flow moves accounts between TecnoSpeed and Finnet only.
 * - `PROVIDER_UNAVAILABLE`: the destination cannot set accounts up from this process.
 * - `FINNET_NOT_ENABLED`: the tenant is not enabled for Finnet.
 * - `FINNET_MAPPING_REQUIRED`: the account has no Finnet mapping and none was given to create.
 */
export const PROVIDER_MIGRATION_BLOCKER_CODES = [
  'IN_FLIGHT',
  'MOVE_NOT_SUPPORTED',
  'PROVIDER_UNAVAILABLE',
  'FINNET_NOT_ENABLED',
  'FINNET_MAPPING_REQUIRED',
] as const;
export type PaymentProviderMigrationBlockerCode = (typeof PROVIDER_MIGRATION_BLOCKER_CODES)[number];

export interface PaymentProviderMigrationBlockers extends ProviderSwitchBlockedDetails {
  codes: PaymentProviderMigrationBlockerCode[];
}

/**
 * What the move does to the account:
 * - `FORWARD`: TecnoSpeed → Finnet;
 * - `REVERSE`: Finnet → TecnoSpeed, restoring the TecnoSpeed rows the forward move suspended;
 * - `REPAIR`: already on Finnet but in ERROR (mapped or enabled afterwards, D17): set it up again;
 * - `NONE`: already on the destination and not in ERROR; nothing to do.
 */
export type PaymentProviderMigrationDirection = 'FORWARD' | 'REVERSE' | 'REPAIR' | 'NONE';

export interface PaymentProviderMigrationCapabilityRow {
  providerId: string;
  instruction: PaymentInstructionKind;
  status: PaymentCapabilityStatus;
  blockedReason: string | null;
}

/**
 * Where the TecnoSpeed rows of a reverse move come back to. `LAST_MIGRATION`: the switch that
 * moved the account to Finnet recorded them, and `rows` are those statuses. `NONE`: no such
 * record exists, so the rows stay SUSPENDED (fail closed) until operations promotes them.
 */
export interface PaymentProviderMigrationRestorePlan {
  source: 'LAST_MIGRATION' | 'NONE';
  /** When the recorded switch to Finnet happened (ISO 8601), for `LAST_MIGRATION`. */
  migratedAt: string | null;
  rows: { instruction: PaymentInstructionKind; status: PaymentCapabilityStatus }[];
}

export interface PaymentProviderMigrationPreview {
  bankAccountId: string;
  fromProviderId: PaymentDispatchProviderId;
  toProviderId: PaymentDispatchProviderId;
  direction: PaymentProviderMigrationDirection;
  paymentsEnabled: boolean;
  paymentSetupStatus: BankAccountPaymentSetupStatus;
  paymentSetupError: string | null;
  /** Finnet's side of the account; null when neither end of the move is Finnet. */
  finnet: { tenantEnabled: boolean; mappedAcoId: number | null } | null;
  blockers: PaymentProviderMigrationBlockers;
  capabilitiesBefore: PaymentProviderMigrationCapabilityRow[];
  /** The rows as the move will leave them (predicted with the providers' own rules). */
  capabilitiesAfter: PaymentProviderMigrationCapabilityRow[];
  /** Rows of the provider the account leaves that the move suspends. */
  willSuspend: PaymentProviderMigrationCapabilityRow[];
  /** For a reverse move only. */
  restore: PaymentProviderMigrationRestorePlan | null;
  /** No blocker and something to do. */
  canExecute: boolean;
}

export interface ExecutePaymentProviderMigrationRequest {
  toProviderId: PaymentDispatchProviderId;
  /** Finnet's `aco_id` to map the account to first, when it has no mapping yet. */
  finnetAcoId?: number;
}

export interface PaymentProviderMigrationResult {
  direction: PaymentProviderMigrationDirection;
  /** A Finnet mapping was created by this call. */
  mappingCreated: boolean;
  /**
   * For a reverse move: `RESTORED` when the recorded statuses were put back, `NO_RECORD` when
   * none existed and the TecnoSpeed rows were left SUSPENDED. Null otherwise.
   */
  restore: 'RESTORED' | 'NO_RECORD' | null;
  paymentsEnabled: boolean;
  paymentSetupStatus: BankAccountPaymentSetupStatus;
  paymentSetupProvider: string | null;
  paymentSetupError: string | null;
  capabilities: PaymentProviderMigrationCapabilityRow[];
}
